import config from "@/config";
import type { PbmDirectoryResponse } from "@/types/pbm-directory";

export type PbmDirectoryQuery = {
  payer_name?: string;
  bin?: string;
  pcn?: string;
  max_documents?: number;
};

export function buildPbmDirectoryQueryString(q: PbmDirectoryQuery): string {
  const p = new URLSearchParams();
  if (q.payer_name?.trim()) p.set("payer_name", q.payer_name.trim());
  if (q.bin?.trim()) p.set("bin", q.bin.trim());
  if (q.pcn?.trim()) p.set("pcn", q.pcn.trim());
  if (q.max_documents != null && q.max_documents > 0) {
    p.set("max_documents", String(q.max_documents));
  }
  return p.toString();
}

export async function fetchPbmDirectory(
  q: PbmDirectoryQuery,
): Promise<PbmDirectoryResponse> {
  const qs = buildPbmDirectoryQueryString(q);
  const res = await fetch(
    `${config.API_BASE_URL}/api/dashboard/payer/list${qs ? `?${qs}` : ""}`,
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<PbmDirectoryResponse>;
}

export type PbmBlockedToggleBody = {
  member_bin: string;
  member_pcn: string;
  phone_number: string;
  blocked: boolean;
};

export type PbmBlockedToggleResponse = {
  success: boolean;
  blocked?: boolean;
  phone_number?: string;
  member_bin?: string;
  member_pcn?: string;
  error?: string;
};

export async function setPbmBlockedNumber(
  body: PbmBlockedToggleBody,
): Promise<PbmBlockedToggleResponse> {
  const res = await fetch(`${config.API_BASE_URL}/api/dashboard/payer/blocked`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as PbmBlockedToggleResponse;
  if (!res.ok) {
    const detail = (data as { detail?: unknown }).detail;
    const errMsg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail
              .map((x) =>
                x && typeof x === "object" && "msg" in x
                  ? String((x as { msg: unknown }).msg)
                  : JSON.stringify(x),
              )
              .join("; ")
          : `Request failed: ${res.status}`;
    return { success: false, error: errMsg };
  }
  return { ...data, success: true };
}

export type PbmPcnPayerNameBody = {
  member_bin: string;
  member_pcn: string;
  payer_name: string;
};

export type PbmPcnPayerNameResponse = {
  success: boolean;
  payer_name?: string;
  member_bin?: string;
  member_pcn?: string;
  error?: string;
};

export async function updatePbmPcnPayerName(
  body: PbmPcnPayerNameBody,
): Promise<PbmPcnPayerNameResponse> {
  const res = await fetch(`${config.API_BASE_URL}/api/dashboard/payer/pcn-payer-name`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as PbmPcnPayerNameResponse;
  if (!res.ok) {
    const detail = (data as { detail?: unknown }).detail;
    const errMsg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail
              .map((x) =>
                x && typeof x === "object" && "msg" in x
                  ? String((x as { msg: unknown }).msg)
                  : JSON.stringify(x),
              )
              .join("; ")
          : `Request failed: ${res.status}`;
    return { success: false, error: errMsg };
  }
  return { ...data, success: true };
}
